import React from 'react';
import { useTranslation } from 'react-i18next';

interface InterviewProgressStepperProps {
  currentQuestionIndex: number;
  totalQuestions: number;
  questions?: Array<{
    index: number;
    time_limit_minutes?: number;
    question?: string;
  }>;
  /** When true, all interview steps are completed and the personality test step is active */
  psychometricActive?: boolean;
  /** Whether there's a personality test step to show after interview questions */
  hasPsychometricStep?: boolean;
}

export const InterviewProgressStepper: React.FC<InterviewProgressStepperProps> = ({
  currentQuestionIndex,
  totalQuestions,
  questions = [],
  psychometricActive = false,
  hasPsychometricStep = false,
}) => {
  const { t } = useTranslation('interview');
  const displaySteps = totalQuestions + (hasPsychometricStep ? 1 : 0);
  const psychometricStepIndex = totalQuestions;

  const headerLabel = psychometricActive
    ? t('live.progress.personality_assessment')
    : t('live.progress.interview_progress');
  const headerDetail = psychometricActive
    ? ''
    : t('live.progress.question_of', {
        current: currentQuestionIndex + 1,
        total: totalQuestions,
      });

  return (
    <div className="w-full bg-white border-b border-gray-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold text-dark-invirtus font-heading">
            {headerLabel}
          </h3>
          {headerDetail && (
            <span className="text-xs text-gray-500 font-body">
              {headerDetail}
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center w-full">
        {Array.from({ length: displaySteps }, (_, index) => {
          const isPsychometricStep = hasPsychometricStep && index === psychometricStepIndex;
          const isActive = isPsychometricStep
            ? psychometricActive
            : !psychometricActive && index === currentQuestionIndex;
          const isCompleted = isPsychometricStep
            ? false
            : psychometricActive || index < currentQuestionIndex;
          const question = !isPsychometricStep ? questions.find(q => q.index === index) : undefined;
          const isLast = index === displaySteps - 1;

          return (
            <React.Fragment key={index}>
              <div className="flex flex-col items-center shrink-0">
                <div
                  className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs font-semibold font-heading transition-all duration-300 ${
                    isCompleted
                      ? 'bg-purple border-purple text-dark-invirtus'
                      : isActive
                      ? 'bg-white border-purple text-purple shadow-lg scale-110'
                      : 'bg-gray-100 border-gray-300 text-gray-400'
                  }`}
                >
                  {isCompleted ? (
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : isPsychometricStep ? (
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 0 1-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 0 1 4.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0 1 12 15a9.065 9.065 0 0 0-6.23.693L5 14.5m14.8.8 1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0 1 12 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
                    </svg>
                  ) : (
                    index + 1
                  )}
                </div>

                {isActive && question?.time_limit_minutes && (
                  <div className="mt-1 px-2 py-0.5 bg-purple/10 border border-purple/20 rounded text-xs text-purple font-body whitespace-nowrap">
                    {question.time_limit_minutes}min
                  </div>
                )}
              </div>

              {!isLast && (
                <div
                  className={`h-0.5 flex-1 mx-1 transition-all duration-300 ${
                    isCompleted ? 'bg-purple' : 'bg-gray-300'
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};
